import React, { Component } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Paper } from "@mui/material";
import {
    accPayEntities, reportColumnChunks, formatAccounting, formatPaddedDate, formatPaddedWeek,
    normalizeWeekCode, unapprovedWeekMap, weekStartDate,
} from '../../helper';
import SectionHeader from '../../Pages/admin/shared/SectionHeader';
import TotalsBar from '../../Pages/admin/shared/TotalsBar';
import NotApprovedTag from '../../Pages/admin/shared/NotApprovedTag';

/* Account Balance — Detail view.
 *
 * Laid out after the legacy report the user supplied (phone-clinic/reportLayout/accountbalance.html,
 * T1.36, 2026-10-26), in the app's current theme:
 *   1. Shops Profit & Loss — Week + 9 shops per block, a Total row per block;
 *   2. Shops Card Amount   — same shape, only when the account has card shops;
 *   3. ONE table holding Payment By Account (5 columns) beside Payment By Shop (6 columns), a navy
 *      "Week : …" bar per week across both, the two sides paired line-by-line within a week;
 *   4. the Balance B/F | P&L | Card | Pay By Acc | Pay By Shop | Net Balance line.
 * Negative amounts are shown in brackets and dates as "06 Sep 2026" (both display only).
 *
 * T1.50 (2026-10-27): matched to the user's second reference export (phone-clinic/reportTEmp.html).
 * Each section is ONE table with every block's header repeated inside it, every block is padded
 * to 9 shop columns, and the column widths and alignments are the file's. These tables are always
 * tables — never cards — and long text wraps inside its column, like the reference: the user's
 * exception to the app-wide "no wrap / cards when it doesn't fit" rules, for this report only
 * (className "pc-ref-table"; CSS in index.css and assets/css/mobile.css). On a phone the same table
 * shrinks to the screen width with small text.
 *
 * Every figure is the one index.js already computed — this component only lays them out.
 */

const SHOPS_PER_BLOCK = 9;
// The reference's widths: Week 13%, each of the 9 shop columns 9.6%.
const WEEK_COL_WIDTH = '13%';
const SHOP_COL_WIDTH = '9.6%';
const TABLE_CLASS = 'report-table report-table--tight pc-ref-table';

const NUM = { textAlign: 'right' };
const CENTER = { textAlign: 'center' };
const WEEK_BAR = { background: '#19284a', color: '#ffffff', fontSize: 12, fontWeight: 700 };
const TOTAL_ROW = { background: '#e7e7e7' };
// Separates Payment By Account (left) from Payment By Shop (right), header to footer.
const DIVIDER = { borderLeft: '2px solid #cbd5e1' };
const BOLD = { fontWeight: 800 };
const CELL = { fontSize: 12 };
// Date · Shop · On Behalf Acc · Description · Amount | Date · Pay Account · Shop · On Behalf Acc · Description · Amount
// The reference's widths (8 8 6 24 8 | 8 6 8 6 18 8) add up to 108%; scaled to 100%, same proportions.
const PAYMENT_COL_WIDTHS = [8, 8, 6, 24, 8, 8, 6, 8, 6, 18, 8].map((w) => `${(w / 1.08).toFixed(2)}%`);
// Its own unbroken word under the name when the column is narrow, rather than split "War|ehouse".
const TAG = { fontSize: 10, color: '#64748b', marginLeft: 4, display: 'inline-block', overflowWrap: 'normal' };

/* A Shop Payments line's stored date, but only when it falls inside the line's own week. Only 22 of
   1,742 stored dates do — the rest look like entry dates — so anything else is left blank rather
   than shown as a misleading payment day (user's choice, 2026-10-26). Ledger rows have no date. */
function dateInsideWeek(date, weekCode) {
    if (!date || !weekCode) return '';
    const monday = weekStartDate(normalizeWeekCode(weekCode));
    if (!monday) return '';
    const sunday = new Date(`${monday}T00:00:00`);
    sunday.setDate(sunday.getDate() + 6);
    const sun = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
    const day = String(date).slice(0, 10);
    return day >= monday && day <= sun ? formatPaddedDate(day) : '';
}

/* An amount is never split (user, 2026-10-28: "32," on one line and "492.00" on the next looked
   awkward). It stays whole; when the table is too wide the whole table is scaled down to fit
   (helper/fitCards.js), not its numbers broken. */
function amount(value) {
    return formatAccounting(value);
}

// A warehouse in an account / shop column, labelled so it is not mistaken for one.
const withWarehouseLabel = (name) => (name ? <>{name}<span className="pc-ref-tag" style={TAG}>Warehouse</span></> : '');

class Detail extends Component {

    // Week code -> not-approved entry (T1.35). Keys are normalised, so look up with any form.
    unapproved() {
        const list = this.props.ReportDetails?.unapprovedWeeks;
        if (this._unapprovedList !== list) {
            this._unapprovedList = list;
            this._unapprovedMap = unapprovedWeekMap(list);
        }
        return this._unapprovedMap;
    }

    /*
     * One table, in stacked blocks of the Week column plus 9 shops (the legacy layout): each block
     * repeats the header row, then its weeks, then its Total row. `columns`, each row's `PnL`, and
     * `totals` are PARALLEL arrays indexed by column, so a block is the same index range sliced out
     * of all three. Every block is padded with blank columns to 9 shops, as in the reference, so
     * the columns line up and keep their widths.
     */
    renderWideTable({ columns, rows, totals, columnLabel, label }) {
        const cols = columns || [];
        const blocks = reportColumnChunks(cols.length, SHOPS_PER_BLOCK + 1);
        if (blocks.length === 0) return null;
        return (
            <Row>
                <Col md={12} >
                    <Paper>
                        <TableContainer className="report-table-container" >
                            <Table size="small" className={TABLE_CLASS} aria-label={label}>
                                <colgroup>
                                    <col style={{ width: WEEK_COL_WIDTH }} />
                                    {Array.from({ length: SHOPS_PER_BLOCK }, (_, i) => <col key={i} style={{ width: SHOP_COL_WIDTH }} />)}
                                </colgroup>
                                {blocks.map(({ start, end }) => {
                                    const pad = SHOPS_PER_BLOCK - (end - start);
                                    const blanks = (key, props) => Array.from({ length: pad }, (_, i) => <TableCell key={`${key}-pad-${i}`} {...props} />);
                                    return (
                                        <TableBody key={`block-${start}`}>
                                            <TableRow>
                                                <TableCell variant="head" style={CENTER} >Week</TableCell>
                                                {cols.slice(start, end).map((x, i) => (
                                                    <TableCell variant="head" key={start + i} style={CENTER} >{columnLabel(x)}</TableCell>
                                                ))}
                                                {blanks('h', { variant: 'head' })}
                                            </TableRow>
                                            {(rows || []).map((x, idx) => (
                                                <TableRow key={idx} >
                                                    <TableCell style={CENTER} >
                                                        {formatPaddedWeek(x.weekNum)}
                                                        <NotApprovedTag info={this.unapproved().get(normalizeWeekCode(x.weekNum))} />
                                                    </TableCell>
                                                    {x.noApprovedSale
                                                        // Placeholder for a not-approved week — no figures (T1.35).
                                                        ? cols.slice(start, end).map((c, i) => (
                                                            <TableCell className="pc-ref-num" key={start + i} style={{ ...NUM, color: '#94a3b8' }}>—</TableCell>
                                                        ))
                                                        : cols.slice(start, end).map((c, i) => (
                                                            <TableCell className="pc-ref-num" key={start + i} style={NUM}>{amount((x.PnL || [])[start + i])}</TableCell>
                                                        ))}
                                                    {blanks(`r${idx}`)}
                                                </TableRow>
                                            ))}
                                            <TableRow style={TOTAL_ROW} >
                                                <TableCell style={BOLD} >Total</TableCell>
                                                {(totals || []).slice(start, end).map((xl, i) => (
                                                    <TableCell className="pc-ref-num" style={{ ...BOLD, ...NUM }} key={start + i}>{amount(isNaN(xl?.totalValue) ? 0 : parseFloat(xl.totalValue))}</TableCell>
                                                ))}
                                                {blanks('t')}
                                            </TableRow>
                                        </TableBody>
                                    );
                                })}
                            </Table>
                        </TableContainer>
                    </Paper>
                </Col>
            </Row>
        );
    }

    // Payment By Account lines grouped by week code. Zero-amount lines are left out, as before.
    payByAccountByWeek() {
        const byWeek = new Map();
        for (const block of (this.props.ReportDetails?.payByAccountList || [])) {
            const week = normalizeWeekCode(block.AP_WEEK);
            const lines = (block.AP_DETAILS || []).filter(l => l.ShopPay_Amnt !== 0);
            byWeek.set(week, [...(byWeek.get(week) || []), ...lines]);
        }
        return byWeek;
    }

    // Payment By Shop rows grouped by week code.
    payByShopByWeek() {
        const byWeek = new Map();
        for (const block of (this.props.ReportDetails?.payByShopList || [])) {
            const week = normalizeWeekCode(block.TR_WEEK);
            byWeek.set(week, [...(byWeek.get(week) || []), ...(block.SP_DETAILS || [])]);
        }
        return byWeek;
    }

    // The five Payment By Account cells for one line, or five blanks.
    accountCells(line, key) {
        if (!line) return Array.from({ length: 5 }, (_, i) => <TableCell key={`${key}-a${i}`} />);
        const { shop, acc, wh } = accPayEntities(line);
        return [
            <TableCell key={`${key}-a0`} style={{ ...CELL, ...CENTER }} >{formatPaddedDate(line.ShopPay_Date) || line.ShopPay_Date}</TableCell>,
            <TableCell key={`${key}-a1`} style={{ ...CELL, ...CENTER }} >{shop}</TableCell>,
            <TableCell key={`${key}-a2`} style={{ ...CELL, ...CENTER }} >{acc}{acc && wh ? ', ' : ''}{withWarehouseLabel(wh)}</TableCell>,
            <TableCell key={`${key}-a3`} style={CELL} >{line.ShopPay_Desc}</TableCell>,
            <TableCell className="pc-ref-num" key={`${key}-a4`} style={{ ...CELL, ...NUM }} >{amount(line.ShopPay_Amnt)}</TableCell>,
        ];
    }

    /* The six Payment By Shop cells for one row, or six blanks. Columns follow legacy
     * SP_MS_FMS_RptAccountTransactions_Balance_Query: a shop paying this account fills Shop; an
     * account paying on this account's behalf fills Pay Account, with this account as On Behalf
     * Acc; a warehouse (none exist today) goes in Shop, labelled. */
    shopCells(row, key) {
        if (!row) return Array.from({ length: 6 }, (_, i) => <TableCell key={`${key}-s${i}`} style={i === 0 ? DIVIDER : undefined} />);
        const type = String(row.TR_TYPE);
        const payAccount = type === '2' ? row.ACC_ID : '';
        const shop = type === '3' ? row.ACC_ID : (type === '4' ? withWarehouseLabel(row.ACC_ID) : '');
        const onBehalf = type === '2' ? (this.props.ReportDetails?.accountTitle || '') : '';
        return [
            <TableCell key={`${key}-s0`} style={{ ...CELL, ...CENTER, ...DIVIDER }} >{dateInsideWeek(row.TR_DATE, row.TR_WEEK)}</TableCell>,
            <TableCell key={`${key}-s1`} style={{ ...CELL, ...CENTER }} >{payAccount}</TableCell>,
            <TableCell key={`${key}-s2`} style={{ ...CELL, ...CENTER }} >{shop}</TableCell>,
            <TableCell key={`${key}-s3`} style={{ ...CELL, ...CENTER }} >{onBehalf}</TableCell>,
            <TableCell key={`${key}-s4`} style={CELL} >{row.TR_DET}</TableCell>,
            <TableCell className="pc-ref-num" key={`${key}-s5`} style={{ ...CELL, ...NUM }} >{amount(row.TR_AMT)}</TableCell>,
        ];
    }

    renderPaymentsTable() {
        const left = this.payByAccountByWeek();
        const right = this.payByShopByWeek();
        const weeks = [...new Set([...left.keys(), ...right.keys()])].filter(Boolean).sort();
        const head = { ...CENTER };
        const body = [];
        weeks.forEach((week) => {
            const a = left.get(week) || [];
            const s = right.get(week) || [];
            body.push(
                <TableRow key={`wk-${week}`} className="table-rows" style={{ background: WEEK_BAR.background }} >
                    <TableCell colSpan={11} style={WEEK_BAR} >
                        Week : {formatPaddedWeek(week)}
                        <NotApprovedTag info={this.unapproved().get(week)} />
                    </TableCell>
                </TableRow>
            );
            for (let i = 0; i < Math.max(a.length, s.length); i++) {
                body.push(
                    <TableRow key={`${week}-${i}`} className="table-rows" >
                        {this.accountCells(a[i], `${week}-${i}`)}
                        {this.shopCells(s[i], `${week}-${i}`)}
                    </TableRow>
                );
            }
        });

        return (
            <Row>
                <Col md={12} >
                    <Paper>
                        <TableContainer className="report-table-container" >
                            <Table className={TABLE_CLASS} size="small" aria-label="payment by account and payment by shop">
                                {/* Fixed layout takes widths from the FIRST row, which here is the two
                                    spanning "Payment By …" cells — so the widths live on a colgroup. */}
                                <colgroup>
                                    {PAYMENT_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
                                </colgroup>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center" colSpan={5} style={{ fontWeight: 700 }} >Payment By Account</TableCell>
                                        <TableCell align="center" colSpan={6} style={{ fontWeight: 700, ...DIVIDER }} >Payment By Shop</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...head }} >Date</TableCell>
                                        <TableCell style={{ ...head }} >Shop</TableCell>
                                        <TableCell style={{ ...head }} >On Behalf Acc</TableCell>
                                        <TableCell style={{ ...head }} >Description</TableCell>
                                        <TableCell style={{ ...head }} >Amount</TableCell>
                                        <TableCell style={{ ...head, ...DIVIDER }} >Date</TableCell>
                                        <TableCell style={{ ...head }} >Pay Account</TableCell>
                                        <TableCell style={{ ...head }} >Shop</TableCell>
                                        <TableCell style={{ ...head }} >On Behalf Acc</TableCell>
                                        <TableCell style={{ ...head }} >Description</TableCell>
                                        <TableCell style={{ ...head }} >Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {body.length === 0 && (
                                        <TableRow><TableCell colSpan={11} align="center" style={{ color: '#64748b' }} >No payments in this range.</TableCell></TableRow>
                                    )}
                                    {body}
                                </TableBody>
                                <TableFooter>
                                    <TableRow style={TOTAL_ROW} >
                                        <TableCell colSpan={4} style={{ ...BOLD, color: '#111827' }} >Total</TableCell>
                                        <TableCell className="pc-ref-num" style={{ ...BOLD, ...NUM, color: '#111827' }} >{amount(this.props.ReportDetails?.pbaTotal)}</TableCell>
                                        <TableCell colSpan={5} style={{ ...BOLD, ...DIVIDER, color: '#111827' }} >Total</TableCell>
                                        <TableCell className="pc-ref-num" style={{ ...BOLD, ...NUM, color: '#111827' }} >{amount(this.props.ReportDetails?.pbsTotal)}</TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Col>
            </Row>
        );
    }

    render() {
        const d = this.props.ReportDetails || {};
        // Same expression as before, character for character (index.js toSummaryRow mirrors it).
        const net = (d.totalPnL + d.pbaTotal + +d.FB) - parseFloat(d.totalCardSum).toFixed(2) - d.pbsTotal;
        const sep = <span style={{ borderRight: '1px solid', margin: '0px 5px' }} ></span>;
        return (
            <section>
                <SectionHeader title={<>Shops Profit & Loss</>} />
                {this.renderWideTable({
                    columns: d.shopDataList,
                    rows: d.weekDaysList,
                    totals: d.totalShopCash,
                    columnLabel: (x) => x?.ShopName?.label,
                    label: 'shops profit and loss',
                })}

                {d.cardWeekList?.length > 0 ?
                    <div>
                        <SectionHeader title={<>Shops Card Amount</>} />
                        {this.renderWideTable({
                            columns: d.cardShops,
                            rows: d.cardWeekList,
                            totals: d.cardTotal,
                            columnLabel: (x) => x,
                            label: 'shops card amount',
                        })}
                    </div>
                    :
                    null
                }

                {/* No title here, as in the reference: the table's own header names both halves. */}
                <div style={{ marginTop: 16 }}>{this.renderPaymentsTable()}</div>

                <TotalsBar>
                    <span>Balance B/F: </span>
                    <span>{formatAccounting(d.FB)}</span>
                    {sep}
                    <span>P&L: </span>
                    <span>{formatAccounting(d.totalPnL)}</span>
                    {sep}
                    <span>Card: </span>
                    <span>{formatAccounting(d.totalCardSum)}</span>
                    {sep}
                    <span>Pay By Acc: </span>
                    <span>{formatAccounting(d.pbaTotal)}</span>
                    {sep}
                    <span>Pay By Shop: </span>
                    <span>{formatAccounting(d.pbsTotal)}</span>
                    {sep}
                    <span style={BOLD} >Net Balance: </span>
                    <span style={BOLD} >{formatAccounting(net)}</span>
                </TotalsBar>
            </section >
        );
    }
}

export default Detail;
